import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as XLSX from "xlsx";
import { toCsv } from "../../shared/csv";

export { csvCell, downloadTextFile, toCsv } from "../../shared/csv";

dayjs.extend(customParseFormat);

/** Fields the API's import row accepts (all cell values are strings). */
export const IMPORT_FIELDS = [
  "itsId",
  "name",
  "gender",
  "dateOfBirth",
  "address",
  "notes",
  "joinedOn",
  "guardian1Name",
  "guardian1Relation",
  "guardian1Phone",
  "guardian1ItsId",
  "guardian2Name",
  "guardian2Relation",
  "guardian2Phone",
  "guardian2ItsId",
  "primaryGuardian",
  "className",
  "section",
  "enrolledOn",
  "rollNo",
  "newAdmission",
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

/** U+FEFF (byte order mark). Kept as a number so no invisible character lives in this file. */
const BOM_CODE = 0xfeff;

const DATE_FIELDS: ReadonlySet<ImportField> = new Set(["dateOfBirth", "joinedOn", "enrolledOn"]);
export const MAX_IMPORT_ROWS = 1000;

/** Accepted header spellings per field, compared after `normalizeHeader`. */
const HEADER_ALIASES: Record<ImportField, string[]> = {
  itsId: ["itsid", "its", "itsno", "itsnumber", "studentits", "studentitsid", "studentid"],
  name: ["name", "studentname", "fullname"],
  gender: ["gender", "sex"],
  dateOfBirth: ["dateofbirth", "dob", "birthdate", "birthday"],
  address: ["address"],
  notes: ["notes", "note", "remarks", "remark", "arabicname"],
  joinedOn: ["joinedon", "joindate", "dateofjoining", "admissiondate"],
  guardian1Name: ["guardian1name", "g1name", "guardian1", "fathername"],
  guardian1Relation: ["guardian1relation", "g1relation"],
  guardian1Phone: [
    "guardian1phone",
    "guardian1whatsapp",
    "guardian1mobile",
    "guardian1number",
    "g1phone",
    "mobileno",
    "mobile",
    "whatsapp",
    "phone",
  ],
  guardian1ItsId: ["guardian1itsid", "guardian1its", "g1its"],
  guardian2Name: ["guardian2name", "g2name", "guardian2"],
  guardian2Relation: ["guardian2relation", "g2relation"],
  guardian2Phone: [
    "guardian2phone",
    "guardian2whatsapp",
    "guardian2mobile",
    "guardian2number",
    "g2phone",
    "contactnos",
    "contactno",
    "contact",
    "altmobile",
    "altphone",
    "telephoneno",
  ],
  guardian2ItsId: ["guardian2itsid", "guardian2its", "g2its"],
  primaryGuardian: ["primaryguardian", "primary"],
  className: ["class", "classname", "classid", "darajah"],
  section: ["section", "sec", "division", "div", "divisionname"],
  enrolledOn: ["enrolledon", "enrolmentdate", "enrollmentdate", "enrolleddate"],
  rollNo: ["rollno", "roll", "rollnumber"],
  newAdmission: ["newadmission", "isnewadmission"],
};

const DATE_FORMATS = [
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MMM-YYYY",
  "D-MMM-YYYY",
  "DD MMM YYYY",
  "D MMM YYYY",
] as const;

export type ImportRowPayload = { rowNumber: number } & Partial<Record<ImportField, string>>;

export type ParsedStudentImport = {
  rows: ImportRowPayload[];
  /** Original header labels as they appear in the file. */
  headers: string[];
  /** Original cell text per row number, for the "rows with problems" download. */
  rawRows: Map<number, string[]>;
  skippedBlankRows: number;
  /** Header labels that matched no known column (ignored, but worth telling the user). */
  unknownHeaders: string[];
};

export function normalizeHeader(value: unknown): string {
  const text = cellToString(value);
  return (text.charCodeAt(0) === BOM_CODE ? text.slice(1) : text)
    .toLowerCase()
    .replace(/[\s\-_./()]+/g, "");
}

export function cellToString(value: unknown): string {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format("YYYY-MM-DD");
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return String(value).trim();
}

/**
 * Turns a date cell (Excel Date/serial, ISO, or common Indian formats) into YYYY-MM-DD.
 * Anything it cannot read is returned as typed so the server reports it against the row.
 */
export function normalizeDateCell(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format("YYYY-MM-DD");
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed && parsed.y > 1900) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
    }
  }
  const raw = cellToString(value);
  if (!raw) return "";
  for (const format of DATE_FORMATS) {
    const parsed = dayjs(raw, format, true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }
  return raw;
}

/** Maps each field to the column index it was found in (first match wins). */
export function mapColumns(headerRow: unknown[]): Partial<Record<ImportField, number>> {
  const normalized = headerRow.map(normalizeHeader);
  const columns: Partial<Record<ImportField, number>> = {};
  const claimed = new Set<number>();
  for (const field of IMPORT_FIELDS) {
    const index = normalized.findIndex(
      (h, idx) => h !== "" && !claimed.has(idx) && HEADER_ALIASES[field].includes(h)
    );
    if (index >= 0) {
      columns[field] = index;
      claimed.add(index);
    }
  }
  return columns;
}

/** Parses the first sheet's grid (header row + data rows). Throws a user-readable Error. */
export function parseStudentImportGrid(grid: unknown[][]): ParsedStudentImport {
  if (grid.length < 2) {
    throw new Error("The file needs a header row and at least one student row");
  }
  const headerRow = grid[0] ?? [];
  const columns = mapColumns(headerRow);
  if (columns.itsId === undefined) {
    throw new Error('No ITS column found. Add a column headed "its_id" (see the template).');
  }

  const used = new Set(Object.values(columns));
  const headers = headerRow.map(cellToString);
  const unknownHeaders = headers.filter((h, i) => h !== "" && !used.has(i));

  const rows: ImportRowPayload[] = [];
  const rawRows = new Map<number, string[]>();
  let skippedBlankRows = 0;

  for (let i = 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const rowNumber = i + 1;
    if (cells.every((c) => cellToString(c) === "")) {
      skippedBlankRows += 1;
      continue;
    }
    const payload: ImportRowPayload = { rowNumber };
    for (const field of IMPORT_FIELDS) {
      const index = columns[field];
      if (index === undefined) continue;
      const cell = cells[index];
      const text = DATE_FIELDS.has(field) ? normalizeDateCell(cell) : cellToString(cell);
      if (text !== "") payload[field] = text;
    }
    rows.push(payload);
    rawRows.set(
      rowNumber,
      headers.map((_, col) => cellToString(cells[col]))
    );
  }

  if (rows.length === 0) throw new Error("No student rows found in the file");
  if (rows.length > MAX_IMPORT_ROWS) {
    throw new Error(
      `The file has ${rows.length} students; import at most ${MAX_IMPORT_ROWS} at a time. Split it into smaller files.`
    );
  }
  return { rows, headers, rawRows, skippedBlankRows, unknownHeaders };
}

/** Parse a CSV or XLSX file in the browser. */
export async function parseStudentImportFile(file: File): Promise<ParsedStudentImport> {
  const buffer = await file.arrayBuffer();
  // raw: true keeps numbers/dates typed so phone numbers and Excel dates are not mangled.
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  if (!sheet) throw new Error("The workbook has no sheets");
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "", raw: true });
  return parseStudentImportGrid(grid);
}

export const STUDENT_IMPORT_TEMPLATE_HEADERS = [
  "its_id",
  "name",
  "gender",
  "date_of_birth",
  "address",
  "joined_on",
  "guardian1_name",
  "guardian1_relation",
  "guardian1_phone",
  "guardian1_its_id",
  "guardian2_name",
  "guardian2_relation",
  "guardian2_phone",
  "guardian2_its_id",
  "primary_guardian",
  "class",
  "section",
  "enrolled_on",
  "roll_no",
  "new_admission",
  "notes",
] as const;

export const STUDENT_IMPORT_TEMPLATE_CSV = toCsv([
  [...STUDENT_IMPORT_TEMPLATE_HEADERS],
  [
    "30123456",
    "Aliasger Fakhri",
    "Male",
    "2015-03-14",
    "12 Main Road, Pune",
    "2026-06-01",
    "Ali Fakhri",
    "Father",
    "+919876543210",
    "30111111",
    "Sara Fakhri",
    "Mother",
    "+919812345678",
    "",
    "1",
    "Class 3",
    "A",
    "2026-06-01",
    "12",
    "no",
    "",
  ],
  [
    "30123457",
    "Fatema Fakhri",
    "Female",
    "",
    "",
    "",
    "Ali Fakhri",
    "Father",
    "+919876543210",
    "",
    "",
    "",
    "",
    "",
    "",
    "Class 3",
    "B",
    "",
    "",
    "",
    "",
  ],
]);

export type ImportRowResult = {
  rowNumber: number;
  itsId: string;
  name: string;
  action: "CREATE_STUDENT" | "ENROLL_ONLY" | "SKIP_EXISTS" | "ERROR";
  errors: string[];
  warnings: string[];
  note: string | null;
  className: string | null;
  section?: string | null;
  enrolledOn: string | null;
  admissionNo?: number;
};

/**
 * CSV of the rows that were NOT imported because of problems: the user's original columns plus an
 * "issues" column, so they can fix the file and upload it again.
 */
export function buildProblemRowsCsv(
  headers: string[],
  rawRows: ReadonlyMap<number, string[]>,
  results: readonly ImportRowResult[]
): string {
  const problems = results.filter((r) => r.action === "ERROR");
  const body = problems.map((r) => [
    ...(rawRows.get(r.rowNumber) ?? headers.map(() => "")),
    r.errors.join("; "),
  ]);
  return toCsv([[...headers, "issues"], ...body]);
}
